import React, { useContext, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { notify } from "react-notify-toast";
import { Container, Row, Col, Form } from "react-bootstrap";
import { UserContext } from "../../../Context/UserContext";
import authToken from "../../../clients/authToken";
import clientAxios from "../../../clients/clientAxios";

const TOKEN_LENGTH = 6;
const DIGIT_POSITIONS = [0, 1, 2, 3, 4, 5];

const tokenWrapperStyle: React.CSSProperties = {
  position: "relative",
  display: "inline-flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "12px",
  padding: "8px 4px",
  cursor: "text",
};

const hiddenInputStyle: React.CSSProperties = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  opacity: 0,
  cursor: "text",
  zIndex: 2,
  caretColor: "transparent",
};

const getDigitBoxStyle = (
  isFocused: boolean,
  isActive: boolean,
  isFilled: boolean
): React.CSSProperties => ({
  width: "52px",
  height: "62px",
  borderRadius: "12px",
  border: isFocused && isActive
    ? "2px solid #2563eb"
    : isFilled
    ? "1.5px solid #94a3b8"
    : "1.5px solid #e2e8f0",
  backgroundColor: isFilled ? "#f8fafc" : "#ffffff",
  boxShadow: isFocused && isActive
    ? "0 0 0 4px rgba(37, 99, 235, 0.15)"
    : "0 2px 6px rgba(15, 23, 42, 0.04)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "#0f172a",
  transition: "all 0.18s ease",
  userSelect: "none",
  position: "relative",
});

const activeCaretStyle: React.CSSProperties = {
  width: "2px",
  height: "24px",
  backgroundColor: "#2563eb",
  borderRadius: "1px",
};

export default function Verify2FA() {
  const [inputCode, setInputCode] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const inputElementRef = useRef<HTMLInputElement | null>(null);
  const { verify2FA } = useContext(UserContext);
  const history = useHistory();

  const sanitizeToken = (rawToken: string): string => {
    return rawToken.replace(/\D/g, "").slice(0, TOKEN_LENGTH);
  };

  const verify2fa = async (tokenUser: string) => {
    try {
      const response = await clientAxios.post("/user/2fa/verify", {
        token: tokenUser,
      });

      if (response.data.success) {
        verify2FA(true);
        authToken(localStorage.getItem("JWT"));
        history.push("/");
      } else {
        notify.show(
          response.data.message || "No se ha podido validar el token",
          "error"
        );
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "No se ha podido validar el token";
      notify.show(errorMessage, "error");
    }
  };

  const updateTokenValue = (rawToken: string) => {
    const sanitizedToken = sanitizeToken(rawToken);
    setInputCode(sanitizedToken);
    if (sanitizedToken.length === TOKEN_LENGTH) {
      verify2fa(sanitizedToken);
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateTokenValue(event.target.value);
  };

  const handleSubmitPaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    updateTokenValue(pastedText);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  };

  const handleFocusContainer = () => {
    if (inputElementRef.current) {
      inputElementRef.current.focus();
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);
  };

  const handleInputBlur = () => {
    setIsInputFocused(false);
  };

  const activeDigitIndex = Math.min(inputCode.length, TOKEN_LENGTH - 1);

  return (
    <Container>
      <Row className="d-flex align-items-center text-center">
        <Col className="w-100 bg-white shadow rounded p-4 mb-5 mx-2">
          <h1 className="display-4">Verificá tu identidad</h1>
          <p className="lead">
            Ingresá desde tu celular a "Google Authenticator" e ingresá el
            codigo bajo el ítem que dice "RollingCode Student Manager"
          </p>

          <Row className="justify-content-center">
            <Col xs={12} md={8} lg={6}>
              <Form className="mt-4 mb-2" onSubmit={handleSubmit}>
                <Form.Group>
                  <Form.Label className="font-weight-bold text-secondary mb-3 d-block">
                    Código de verificación de 6 dígitos
                  </Form.Label>
                  <div
                    style={tokenWrapperStyle}
                    onClick={handleFocusContainer}
                  >
                    <Form.Control
                      ref={inputElementRef}
                      value={inputCode}
                      onPaste={handleSubmitPaste}
                      onChange={handleOnChange}
                      onFocus={handleInputFocus}
                      onBlur={handleInputBlur}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      autoComplete="one-time-code"
                      maxLength={TOKEN_LENGTH}
                      aria-label="Token de Google Authenticator"
                      style={hiddenInputStyle}
                      autoFocus
                    />
                    {DIGIT_POSITIONS.map((digitIndex) => {
                      const digitCharacter = inputCode[digitIndex] || "";
                      const isActiveBox = digitIndex === activeDigitIndex;
                      const isFilledBox = Boolean(digitCharacter);

                      return (
                        <div
                          key={digitIndex}
                          style={getDigitBoxStyle(
                            isInputFocused,
                            isActiveBox,
                            isFilledBox
                          )}
                        >
                          {digitCharacter || (
                            isInputFocused && isActiveBox ? (
                              <span style={activeCaretStyle} />
                            ) : null
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
}
